/**
 * BMI calculator logic.
 * Uses metric internally (cm, kg). All conversions preserved on toggle.
 */

export type BMIStandard = "asian" | "who";
export type Gender = "male" | "female";
export type UnitSystem = "metric" | "imperial";

export type CategoryKey =
  | "underweight"
  | "normal"
  | "overweight_at_risk" // Asian only
  | "overweight"
  | "obese_1"
  | "obese_2"
  | "obese_3";

export interface BMIResult {
  bmi: number; // 1 decimal
  category: CategoryKey;
  idealMinKg: number;
  idealMaxKg: number;
  /** positive = lose to reach upper normal; negative = gain to reach lower normal; 0 = within range */
  deltaKg: number;
  targetBmi: number; // BMI value the deltaKg targets
  /** 0-1 normalized position on a 12..40 BMI gauge */
  gaugePosition: number;
}

export type BMIError =
  | "height_invalid"
  | "weight_invalid"
  | "age_invalid"
  | "missing";

/* ---------- Conversions ---------- */

export const KG_PER_LB = 0.45359237;
export const CM_PER_IN = 2.54;

export const lbToKg = (lb: number) => lb * KG_PER_LB;
export const kgToLb = (kg: number) => kg / KG_PER_LB;
export const inToCm = (inches: number) => inches * CM_PER_IN;
export const cmToIn = (cm: number) => cm / CM_PER_IN;

/** Returns [feet, inches] from total inches */
export function inchesToFtIn(totalIn: number): { ft: number; inch: number } {
  const ft = Math.floor(totalIn / 12);
  const inch = +(totalIn - ft * 12).toFixed(1);
  return { ft, inch };
}

/* ---------- Validation ---------- */

export function validateBMI(opts: {
  heightCm: number | null;
  weightKg: number | null;
  age: number | null;
}): { ok: true } | { ok: false; error: BMIError } {
  if (opts.heightCm == null || opts.weightKg == null) return { ok: false, error: "missing" };
  if (opts.age != null && (opts.age < 2 || opts.age > 120)) return { ok: false, error: "age_invalid" };
  if (opts.heightCm < 50 || opts.heightCm > 250) return { ok: false, error: "height_invalid" };
  if (opts.weightKg < 10 || opts.weightKg > 300) return { ok: false, error: "weight_invalid" };
  return { ok: true };
}

/* ---------- Calculation ---------- */

export function calcBMI(heightCm: number, weightKg: number): number {
  const m = heightCm / 100;
  return weightKg / (m * m);
}

export function classifyBMI(bmi: number, standard: BMIStandard): CategoryKey {
  if (standard === "asian") {
    if (bmi < 18.5) return "underweight";
    if (bmi < 23) return "normal";
    if (bmi < 25) return "overweight_at_risk";
    if (bmi < 30) return "obese_1";
    return "obese_2";
  }
  // WHO
  if (bmi < 18.5) return "underweight";
  if (bmi < 25) return "normal";
  if (bmi < 30) return "overweight";
  if (bmi < 35) return "obese_1";
  if (bmi < 40) return "obese_2";
  return "obese_3";
}

/** Normal-range upper/lower BMI bounds per standard. */
export function normalRange(standard: BMIStandard): { lo: number; hi: number } {
  return standard === "asian" ? { lo: 18.5, hi: 22.9 } : { lo: 18.5, hi: 24.9 };
}

export function idealWeightKg(heightCm: number, standard: BMIStandard) {
  const m = heightCm / 100;
  const { lo, hi } = normalRange(standard);
  return { min: +(lo * m * m).toFixed(1), max: +(hi * m * m).toFixed(1) };
}

export function computeBMI(opts: {
  heightCm: number;
  weightKg: number;
  standard: BMIStandard;
}): BMIResult {
  const bmiRaw = calcBMI(opts.heightCm, opts.weightKg);
  const bmi = +bmiRaw.toFixed(1);
  const category = classifyBMI(bmiRaw, opts.standard);
  const { min: idealMinKg, max: idealMaxKg } = idealWeightKg(opts.heightCm, opts.standard);
  const { lo, hi } = normalRange(opts.standard);

  let deltaKg = 0;
  let targetBmi = bmi;
  if (opts.weightKg > idealMaxKg) {
    deltaKg = +(opts.weightKg - idealMaxKg).toFixed(1); // lose
    targetBmi = hi;
  } else if (opts.weightKg < idealMinKg) {
    deltaKg = -+(idealMinKg - opts.weightKg).toFixed(1); // gain (negative)
    targetBmi = lo;
  }

  const min = 12;
  const max = 40;
  const gaugePosition = Math.max(0, Math.min(1, (bmiRaw - min) / (max - min)));

  return { bmi, category, idealMinKg, idealMaxKg, deltaKg, targetBmi, gaugePosition };
}

/* ---------- Gauge segments ---------- */

export function gaugeSegments(standard: BMIStandard): {
  key: CategoryKey;
  from: number;
  to: number;
  color: string;
}[] {
  // span 12..40
  if (standard === "asian") {
    return [
      { key: "underweight", from: 12, to: 18.5, color: "bg-sky-400" },
      { key: "normal", from: 18.5, to: 23, color: "bg-emerald-500" },
      { key: "overweight_at_risk", from: 23, to: 25, color: "bg-yellow-400" },
      { key: "obese_1", from: 25, to: 30, color: "bg-orange-500" },
      { key: "obese_2", from: 30, to: 40, color: "bg-red-600" },
    ];
  }
  return [
    { key: "underweight", from: 12, to: 18.5, color: "bg-sky-400" },
    { key: "normal", from: 18.5, to: 25, color: "bg-emerald-500" },
    { key: "overweight", from: 25, to: 30, color: "bg-yellow-400" },
    { key: "obese_1", from: 30, to: 35, color: "bg-orange-500" },
    { key: "obese_2", from: 35, to: 40, color: "bg-red-500" },
    { key: "obese_3", from: 40, to: 45, color: "bg-red-700" },
  ];
}
